import { ErrorBoundary } from '~/components/Tools';
import { compose } from 'recompose';
import { LoadRemote } from '@mindtickle/ui-shell-core/DynamicRemote';

import { withAsyncRendering } from '~/components/AsyncRenderer';
import { withRouteLevelIntegration } from '~/components/Integrations';

import {
  DEFAULT_ERROR_FALLBACK as ErrorFallback,
  PERFORMANCE_MEASURE_NAMES,
  REMOTE_FILE,
} from '~/config/app.constants';

import { REMOTE_TYPES, TMicroAppDetails, TRoutes, TMicroAppDetailsWithChildren } from '../types';
import { isAdminSite } from '~/utils';
import { getExtendedLogger } from '@mindtickle/ui-shell-core/Logger';
import logger from '~/config/app.logger';
import { sendCustomTimingToDatadog } from '~/components/Datadog/helpers';

const log = getExtendedLogger('routes-config', logger);

const remotes = require('../../../../remotes.js');

const onMicroAppRender = () => {
  sendCustomTimingToDatadog(PERFORMANCE_MEASURE_NAMES.REMOTE_APP_RENDERING_STARTED);
  return null;
};

const createMicroAppRouteConfig = (
  MicroApp: React.ComponentClass<any, any>,
  microAppDetails: TMicroAppDetails
) => {
  const { path, moduleName, remoteName, host } = microAppDetails;
  return {
    path: path,
    element: (
      <ErrorBoundary
        fallback={({ error, ...rest }) => (
          <ErrorFallback error={error} {...rest} message="Error captured in Remote Routing" />
        )}
      >
        <MicroApp
          remote={{
            url: `${host}/${REMOTE_FILE}`,
            moduleName,
            remoteName,
          }}
          lazy={false}
          onIdleEnabled={false}
          onDownloadCompleted={onMicroAppRender}
        />
      </ErrorBoundary>
    ),
    ...microAppDetails,
  };
};

const composeMicroApp = ({
  appName,
  streamName,
  collectorUrl,
  enableRedux,
  graphqlEndpoint,
  enableSubscription,
}: TMicroAppDetailsWithChildren) =>
  compose<never, any>(
    withRouteLevelIntegration<never>({
      appName,
      streamName,
      graphqlEndpoint,
      collectorUrl,
      enableRedux,
      // TODO: Need to remove hardcoded value for datadog
      enableDatadog: true,
      enableSubscription,
    }),
    withAsyncRendering<never>()
  )(LoadRemote);

//Seggregating remotes based on learner and admin remote flags
const segregateRemotes = (remotes: TMicroAppDetails[]) =>
  remotes.filter(({ remoteType = REMOTE_TYPES.BOTH }: TMicroAppDetails) => {
    if (remoteType === REMOTE_TYPES.BOTH) {
      return true;
    } else {
      return (
        (isAdminSite() && remoteType === REMOTE_TYPES.ADMIN) ||
        (!isAdminSite() && remoteType === REMOTE_TYPES.LEARNER)
      );
    }
  });

const getRoutes = (segregatedRemotes: TMicroAppDetailsWithChildren[]): TRoutes =>
  segregatedRemotes
    .filter(
      ({ path, host, remoteName, moduleName }: TMicroAppDetailsWithChildren) =>
        path && host && remoteName && moduleName
    )
    .reduce(
      (routeConfigs: TRoutes, microApp: TMicroAppDetailsWithChildren) => {
        const microAppDetails = { ...microApp };
        if (microAppDetails.children)
          microAppDetails.children = getRoutes(
            segregateRemotes(microAppDetails.children as TMicroAppDetails[])
          );
        const ComposedMicroApp = composeMicroApp(microAppDetails);
        const microAppRouteConfig = createMicroAppRouteConfig(
          ComposedMicroApp,
          microAppDetails as TMicroAppDetails
        );
        if (microAppDetails.isPublic) {
          routeConfigs.publicRoutes.push(microAppRouteConfig);
        } else if (microAppDetails.isProtected) {
          routeConfigs.protectedRoutes.push(microAppRouteConfig);
        } else if (microAppDetails.isShareable) {
          routeConfigs.shareableRoutes.push(microAppRouteConfig);
        } else {
          routeConfigs.protectedRoutes.push(microAppRouteConfig);
        }
        return routeConfigs;
      },
      {
        protectedRoutes: [],
        publicRoutes: [],
        shareableRoutes: [],
      }
    );

const allRoutes = getRoutes(segregateRemotes(remotes));
log('segregated-remotes', segregateRemotes(remotes));
log('final-routes', allRoutes);

export default allRoutes;
